import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { waLink } from "../lib/scroll";
import { BAKES as ITEMS } from "../lib/bakes";

export default function Menu() {
  const [active, setActive] = useState(0);

  return (
    <section id="menu" className="scroll-mt-24 bg-ink text-eggshell py-24 sm:py-36 rounded-t-[32px] sm:rounded-t-[56px]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14 sm:mb-20"
        >
          <div>
            <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-gold mb-4">
              The Bake — Menu
            </p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight">
              What comes out
              <span className="italic text-gold"> of the oven.</span>
            </h2>
          </div>
          <p className="text-eggshell/60 text-sm max-w-xs leading-relaxed">
            Hover the list, tap what you crave — every order goes straight to
            our WhatsApp.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* List */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            {ITEMS.map((item, i) => (
              <motion.a
                key={item.id}
                href={waLink(item.msg)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-card-item"
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className={`group block border-t border-eggshell/15 py-7 sm:py-9 first:border-t-[2px] first:border-gold/60 transition-colors duration-300 ${
                  active === i ? "text-gold" : ""
                }`}
              >
                <div className="flex items-start gap-4 sm:gap-6">
                  <span className="font-mono text-[11px] text-eggshell/40 pt-2.5">
                    ({item.id})
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-4">
                      <h3 className="font-display font-bold text-2xl sm:text-4xl tracking-tight leading-tight transition-transform duration-500 group-hover:translate-x-2">
                        {item.name}
                      </h3>
                      <span className="flex items-center gap-4 sm:gap-6 shrink-0">
                        <span
                          data-testid="product-price"
                          className="font-mono text-sm sm:text-lg text-gold whitespace-nowrap"
                        >
                          ₹{item.price}
                        </span>
                        <ArrowUpRight
                          size={26}
                          className={`shrink-0 transition-all duration-300 ${
                            active === i ? "text-gold rotate-0" : "text-eggshell/30 -rotate-45"
                          }`}
                        />
                      </span>
                    </div>
                    <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-eggshell/40">
                      {item.tag}
                    </p>
                    <p className="mt-3 text-eggshell/65 text-sm leading-relaxed max-w-md">
                      {item.desc}
                    </p>
                    {/* Mobile image */}
                    <div className="lg:hidden mt-5 overflow-hidden rounded-2xl border-2 border-eggshell/15">
                      <img
                        src={item.img}
                        alt={item.name}
                        loading="lazy"
                        className="w-full aspect-[16/10] object-cover"
                      />
                    </div>
                    <span
                      data-testid="product-order-whatsapp-button"
                      className="inline-block mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/90 border-b border-gold/50 pb-1"
                    >
                      Order this on WhatsApp
                    </span>
                  </div>
                </div>
              </motion.a>
            ))}

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.8 }}
              data-testid="menu-also-baked"
              className="border-t border-eggshell/15 pt-6 mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-eggshell/45 leading-relaxed"
            >
              Also on the oven — Pave Chocolates &amp; Olive Focaccia ·{" "}
              <a
                href={waLink("Hi Zoni's Bakehouse! What's the price for Pave Chocolates and the Olive Focaccia?")}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gold border-b border-gold/50 pb-0.5"
              >
                Price on WhatsApp
              </a>
            </motion.p>
          </div>

          {/* Spotlight image (desktop) */}
          <div className="hidden lg:block lg:col-span-5 order-1 lg:order-2 lg:sticky lg:top-28">
            <div className="relative aspect-[4/5] overflow-hidden rounded-[28px] border-[4px] border-gold/40 shadow-[12px_12px_0_rgba(212,163,89,0.15)]">
              <AnimatePresence mode="wait">
                <motion.img
                  key={ITEMS[active].img}
                  src={ITEMS[active].img}
                  alt={ITEMS[active].name}
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/80 to-transparent p-6 pt-14">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
                  {ITEMS[active].tag}
                </p>
                <p className="font-display italic text-xl mt-1">{ITEMS[active].name}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
